import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import supabase from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiTwitter, FiInstagram, FiFacebook, FiShare2, FiSave, FiCheckCircle, FiAlertCircle, FiMessageSquare } from 'react-icons/fi';
import { motion } from 'framer-motion';

const SocialIntegrations = () => {
  const { profile } = useAppContext();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings_20240320')
        .select('*')
        .filter('key', 'ilike', 'social_%');
      
      if (error) throw error;
      
      const settingsMap = {};
      data.forEach(item => {
        settingsMap[item.key] = item.value;
      });
      setSettings(settingsMap);
    } catch (err) {
      console.error('Error fetching social settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('site_settings_20240320')
        .upsert({ key, value: settings[key], updated_at: new Date() });
      
      if (error) throw error;
      setMessage({ type: 'success', text: 'Settings updated successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, field, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  if (profile?.role !== 'admin') return <div className="p-12 text-center font-black uppercase text-stone-400">Restricted Access</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <header className="mb-12">
        <h1 className="text-4xl font-black text-stone-900 tracking-tighter uppercase mb-2">Social Connect</h1>
        <p className="text-stone-500 font-bold uppercase tracking-widest text-xs">Manage API connections for automated job promotion</p>
      </header>

      {message && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className={`mb-8 p-4 rounded-2xl flex items-center gap-3 font-bold uppercase tracking-widest text-xs ${
            message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
          <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} />
          {message.text}
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Twitter / X */}
        <div className="bg-white rounded-[32px] border border-stone-200 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center text-white">
              <SafeIcon icon={FiTwitter} className="text-xl" />
            </div>
            <div>
              <h3 className="font-black text-stone-900 uppercase tracking-widest">X (Twitter)</h3>
              <p className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">v2 API Integration</p>
            </div>
          </div>
          <div className="space-y-4">
            <Input label="API Key" value={settings.social_twitter_keys?.apiKey} onChange={(v) => updateSetting('social_twitter_keys', 'apiKey', v)} />
            <Input label="API Secret" value={settings.social_twitter_keys?.apiSecret} onChange={(v) => updateSetting('social_twitter_keys', 'apiSecret', v)} type="password" />
            <Input label="Access Token" value={settings.social_twitter_keys?.accessToken} onChange={(v) => updateSetting('social_twitter_keys', 'accessToken', v)} />
            <button onClick={() => handleSave('social_twitter_keys')} disabled={saving}
              className="w-full mt-4 py-4 bg-stone-900 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-stone-800 transition-all flex items-center justify-center gap-2">
              <SafeIcon icon={FiSave} /> Save Twitter Keys
            </button>
          </div>
        </div>

        {/* Bluesky */}
        <div className="bg-white rounded-[32px] border border-stone-200 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-500 rounded-2xl flex items-center justify-center text-white">
              <SafeIcon icon={FiShare2} className="text-xl" />
            </div>
            <div>
              <h3 className="font-black text-stone-900 uppercase tracking-widest">Bluesky</h3>
              <p className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">AT Protocol</p>
            </div>
          </div>
          <div className="space-y-4">
            <Input label="Handle (e.g. adventure.bsky.social)" value={settings.social_bluesky_keys?.handle} onChange={(v) => updateSetting('social_bluesky_keys', 'handle', v)} />
            <Input label="App Password" value={settings.social_bluesky_keys?.appPassword} onChange={(v) => updateSetting('social_bluesky_keys', 'appPassword', v)} type="password" />
            <button onClick={() => handleSave('social_bluesky_keys')} disabled={saving}
              className="w-full mt-4 py-4 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
              <SafeIcon icon={FiSave} /> Save Bluesky Keys
            </button>
          </div>
        </div>

        {/* Meta (Instagram/Facebook) */}
        <div className="bg-white rounded-[32px] border border-stone-200 p-8 shadow-sm md:col-span-2">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500 rounded-2xl flex items-center justify-center text-white">
              <SafeIcon icon={FiInstagram} className="text-xl" />
            </div>
            <div>
              <h3 className="font-black text-stone-900 uppercase tracking-widest">Meta Integrations</h3>
              <p className="text-[10px] text-stone-400 font-bold uppercase tracking-widest">Instagram, Facebook Groups & Threads</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input label="Meta Access Token" value={settings.social_meta_keys?.accessToken} onChange={(v) => updateSetting('social_meta_keys', 'accessToken', v)} type="password" />
              <Input label="Instagram Business ID" value={settings.social_meta_keys?.instagramId} onChange={(v) => updateSetting('social_meta_keys', 'instagramId', v)} />
            </div>
            <div className="space-y-4">
              <Input label="Facebook Page ID" value={settings.social_meta_keys?.pageId} onChange={(v) => updateSetting('social_meta_keys', 'pageId', v)} />
              <Input label="Facebook Group ID" value={settings.social_meta_keys?.groupId} onChange={(v) => updateSetting('social_meta_keys', 'groupId', v)} />
            </div>
          </div>
          <button onClick={() => handleSave('social_meta_keys')} disabled={saving}
            className="w-full mt-8 py-4 bg-pink-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-pink-700 transition-all flex items-center justify-center gap-2">
            <SafeIcon icon={FiSave} /> Update Meta Connections
          </button>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text" }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-black text-stone-400 uppercase tracking-widest ml-1">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-xs font-bold text-stone-900 focus:ring-2 focus:ring-emerald-500 outline-none transition-all" />
  </div>
);

export default SocialIntegrations;